/* eslint-disable react/jsx-closing-tag-location */
import React, { useState, useEffect } from 'react'
import useImg from '../../hooks/useImg'

import ImgViewer, { useLightboxState, IconButton } from 'yet-another-react-lightbox'
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen'
import Counter from 'yet-another-react-lightbox/plugins/counter'
import Download from 'yet-another-react-lightbox/plugins/download'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import { zoomProp } from '../../constants/zoomProp'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { PrintIcon, RotateLeft, RotateRight } from '../../constants/imgIcons'

import './Img.css'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/counter.css'
import 'react-lazy-load-image-component/src/effects/blur.css'
import { type ImageData } from '../../services/fetchNameUrls'
import { fetchImageFile } from '../../helpers/imageFiles'

import { Bounce } from 'react-activity'
import 'react-activity/dist/library.css'

import VideoWebFrame from './videoFrame'

const PrinterButton: React.FC = () => {
  const { currentSlide } = useLightboxState()

  const handlePrint = (): void => {
    if (currentSlide == null) return

    const printFrame = document.createElement('iframe')
    printFrame.style.position = 'absolute'
    printFrame.style.width = '0px'
    printFrame.style.height = '0px'
    printFrame.style.border = 'none'
    document.body.appendChild(printFrame)

    const printDocument = printFrame.contentWindow?.document
    if (printDocument != null) {
      printDocument.open()
      printDocument.write(`
        <html>
          <head>
            <title>Print Image</title>
            <style>
              @media print {
                html, body {
                  margin: 0;
                  padding: 0;
                  width: 100%;
                  height: 100%;
                }
                .print-container {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  width: 100vw;
                  height: 100vh;
                  overflow: hidden;
                }
                .print-container img {
                  max-width: 100%;
                  max-height: 100%;
                  object-fit: contain;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-container">
              <img src="${currentSlide.src}" alt="Slide Image" />
            </div>
          </body>
        </html>
      `)
      printDocument.close()

      printFrame.onload = () => {
        printFrame.contentWindow?.print()
        document.body.removeChild(printFrame)
      }
    }
  }

  return (
    <IconButton
      label='Print'
      icon={PrintIcon}
      disabled={currentSlide == null}
      onClick={handlePrint}
    />
  )
}

interface ImgProps {
  imgNames: ImageData[]
  disableGeneralLoad: () => void
}

const Img: React.FC<ImgProps> = ({ imgNames, disableGeneralLoad }) => {
  const [urlArray, error] = useImg(imgNames)
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const [rotation, setRotation] = useState(0)
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<number, boolean>>({})
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [actionBusy, setActionBusy] = useState(false)
  const [downloadHint, setDownloadHint] = useState('')

  const rotateClockwise = (): void => {
    setRotation(prevRotation => prevRotation + 90)
  }

  const rotateCounterClockwise = (): void => {
    setRotation(prevRotation => prevRotation - 90)
  }

  useEffect(() => {
    document.documentElement.style.setProperty('--image-rotation', `${rotation}deg`)
  }, [rotation])

  const handleClick = (index: number): void => {
    setIndex(index)
    setOpen(true)
  }

  const handleClose = (): void => {
    setOpen(false)
    setRotation(0)
  }

  const updateIndex = ({ index: current }: { index: number }): void => {
    setRotation(0)
    setIndex(current)
  }

  // Only include photos in the lightbox slides (videos use native fullscreen)
  const newUrlArray = urlArray.map((media) => {
    return { alt: media.alt, src: media.src, type: media?.type }
  })

  const photoSlides = newUrlArray
    .filter((media) => (media.type ?? 'photo') === 'photo')
    .map((media) => ({ src: media.src }))

  const handleImageLoadStart = (index: number): void => {
    disableGeneralLoad()
    setImageLoadingStates(prev => ({ ...prev, [index]: true }))
  }

  const handleImageLoadEnd = (index: number): void => {
    setImageLoadingStates(prev => ({ ...prev, [index]: false }))
  }

  const handleMouseEnter = (index: number): void => {
    setHoveredIndex(index)
  }

  const handleMouseLeave = (): void => {
    setHoveredIndex(null)
  }

  // Get the photo index for the lightbox (skipping videos)
  const getPhotoIndex = (mediaIndex: number): number => {
    let photoIndex = 0
    for (let i = 0; i < mediaIndex; i++) {
      if ((newUrlArray[i]?.type ?? 'photo') === 'photo') {
        photoIndex++
      }
    }
    return photoIndex
  }

  const toggleSelected = (src: string): void => {
    setSelected(previous => {
      const next = new Set(previous)
      if (next.has(src)) {
        next.delete(src)
      } else {
        next.add(src)
      }
      return next
    })
  }

  const clearSelection = (): void => {
    setSelected(new Set())
  }

  // The selected images as real files, fetched straight from S3 (no backend
  // involved; see fetchImageFile for why the browser cache is bypassed).
  const fetchSelectedFiles = async (): Promise<File[]> => {
    const selectedMedia = newUrlArray.filter(
      (media) => (media.type ?? 'photo') === 'photo' && selected.has(media.src)
    )
    return await Promise.all(
      selectedMedia.map(async (media) => await fetchImageFile(media.src, media.alt))
    )
  }

  // Attaching into an email (including replies in an existing thread) can
  // only happen through real files — no web API reaches the mail client's
  // compose window, and clipboard images always paste inline. So: save the
  // selection as files, the user drags them from Downloads into the email.
  const handleDownloadSelected = async (): Promise<void> => {
    setActionBusy(true)
    try {
      const files = await fetchSelectedFiles()
      for (const file of files) {
        const objectUrl = URL.createObjectURL(file)
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = file.name
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        // revoke later: revoking synchronously can cancel the download
        setTimeout(() => URL.revokeObjectURL(objectUrl), 30000)
      }
      setDownloadHint(
        'Saved to Downloads. Drag the files from the browser downloads menu into your email.'
      )
      setTimeout(() => setDownloadHint(''), 12000)
    } catch (error) {
      console.error('Download failed:', error)
      window.alert('Could not download the selected images. Please try again.')
    } finally {
      setActionBusy(false)
    }
  }

  const photoCount = newUrlArray.filter(
    (media) => (media.type ?? 'photo') === 'photo'
  ).length

  return (
    <>
      {photoCount > 0 && (
        <div className='image-select-bar'>
          {downloadHint !== '' && (
            <span className='image-select-hint'>{downloadHint}</span>
          )}
          <span className='image-select-count'>{selected.size} selected</span>
          <button
            type='button'
            className='image-select-button'
            disabled={selected.size === 0 || actionBusy}
            onClick={() => { void handleDownloadSelected() }}
          >
            Download
          </button>
          <button
            type='button'
            className='image-select-button'
            disabled={selected.size === 0 || actionBusy}
            onClick={clearSelection}
          >
            Clear
          </button>
        </div>
      )}
      <div className='image-grid'>
        {(error !== '') && <p>{error}</p>}
        {newUrlArray.length > 0 && newUrlArray.map((media, index) => (
          <div
            key={media.src}
            className='media-container'
            onMouseEnter={() => media.type === 'video' ? handleMouseEnter(index) : undefined}
            onMouseLeave={handleMouseLeave}
          >
            {(media.type ?? 'photo') === 'photo'
              ? (
                <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {imageLoadingStates[index] && (
                    <div className='image-loading-overlay'>
                      <Bounce color='#4285f4' size={30} />
                    </div>
                  )}
                  <label
                    className='select-image-label'
                    title='Select image'
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type='checkbox'
                      className='select-image-checkbox'
                      checked={selected.has(media.src)}
                      onChange={() => toggleSelected(media.src)}
                    />
                  </label>
                  <LazyLoadImage
                    key={media.src}
                    effect='blur'
                    src={media.src}
                    alt={media.alt}
                    onClick={() => handleClick(getPhotoIndex(index))}
                    beforeLoad={() => handleImageLoadStart(index)}
                    onLoad={() => handleImageLoadEnd(index)}
                    onError={() => handleImageLoadEnd(index)}
                    style={{
                      borderRadius: '8px',
                      maxHeight: '300px',
                      maxWidth: '300px',
                      cursor: 'pointer',
                      transition: 'scale 0.3s ease-in-out',
                      display: imageLoadingStates[index] ? 'none' : 'block'
                    }}
                  />
                </div>
                )
              : (
                <div className='video-frame'>
                  {imageLoadingStates[index] && (
                    <div className='video-loading-overlay'>
                      <Bounce color='#4285f4' size={30} />
                    </div>
                  )}
                  <VideoWebFrame
                    src={media.src}
                    index={index}
                    isHovered={hoveredIndex === index}
                    isLoading={!!imageLoadingStates[index]}
                    onLoadStart={() => handleImageLoadStart(index)}
                    onLoadEnd={() => handleImageLoadEnd(index)}
                  />
                </div>
                )}
          </div>
        ))}

        <div>
          <ImgViewer
            className='image-box'
            on={{
              view: updateIndex
            }}
            animation={{ zoom: zoomProp.animationDuration }}
            zoom={{
              maxZoomPixelRatio: zoomProp.maxZoomPixelRatio,
              zoomInMultiplier: zoomProp.zoomInMultiplier,
              doubleTapDelay: zoomProp.doubleTapDelay,
              doubleClickDelay: zoomProp.doubleClickDelay,
              doubleClickMaxStops: zoomProp.doubleClickMaxStops,
              keyboardMoveDistance: zoomProp.keyboardMoveDistance,
              wheelZoomDistanceFactor: zoomProp.wheelZoomDistanceFactor,
              pinchZoomDistanceFactor: zoomProp.pinchZoomDistanceFactor,
              scrollToZoom: zoomProp.scrollToZoom

            }}
            index={index}
            plugins={[Fullscreen, Counter, Download, Zoom]}
            counter={{ container: { style: { top: 'unset', bottom: 0 } } }}
            toolbar={{
              buttons: [
                <button
                  key='rotate-left'
                  type='button'
                  className='yarl__button'
                  onClick={rotateCounterClockwise}
                >
                  <RotateLeft />
                </button>,
                <button
                  key='rotate-right'
                  type='button'
                  className='yarl__button'
                  onClick={rotateClockwise}
                >
                  <RotateRight />
                </button>,
                <PrinterButton key='printer' />,

                'close'
              ]

            }}
            open={open}
            close={() => { handleClose() }}
            slides={photoSlides}
          />
        </div>
      </div>
    </>

  )
}

export default Img
